import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { triggerApi, agentApi, type TriggerDefinition, type TriggerType } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Radio, ArrowLeft, Save, Trash, Zap, Check, Copy, Eye, EyeOff } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/toast';
import styles from './_auth.triggers.detail.module.css';

export const Route = createFileRoute('/_auth/triggers/$triggerId')({
  component: TriggerDetailPage,
});

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

export default function TriggerDetailPage() {
  const { triggerId } = Route.useParams();
  const { platformUser } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    agentId: '',
    triggerType: 'SCHEDULED' as TriggerType,
    active: true,
    config: {} as any,
  });

  const { data: trigger, isLoading } = useQuery({
    queryKey: ['triggers', triggerId],
    queryFn: () => triggerApi.get(triggerId),
  });

  const { data: executions = [] } = useQuery({
    queryKey: ['triggers', triggerId, 'executions'],
    queryFn: () => triggerApi.executions(triggerId),
  });

  const { data: agents = [] } = useQuery({
    queryKey: ['agents'],
    queryFn: agentApi.list,
  });

  useEffect(() => {
    if (trigger) {
      setForm({
        name: trigger.name,
        description: trigger.description ?? '',
        agentId: trigger.agentId,
        triggerType: trigger.triggerType,
        active: trigger.active,
        config: trigger.config || {},
      });
    }
  }, [trigger]);

  const updateMutation = useMutation({
    mutationFn: (body: Partial<TriggerDefinition>) =>
      triggerApi.update(triggerId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['triggers', triggerId] });
      toast.success('Trigger updated successfully');
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to update trigger');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => triggerApi.delete(triggerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['triggers'] });
      navigate({ to: '/triggers' });
    },
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.agentId) return;
    updateMutation.mutate(form);
  };

  const setConfigValue = (key: string, val: any) => {
    setForm((f) => ({
      ...f,
      config: {
        ...f.config,
        [key]: val,
      },
    }));
  };

  const handleCopyUrl = () => {
    const url = `${API_BASE}/v1/t/${platformUser?.tenant.slug}/webhooks/${triggerId}`;
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleCopySecret = () => {
    if (form.config?.secret) {
      navigator.clipboard.writeText(form.config.secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  if (isLoading || !trigger) {
    return <div className={styles.loading}>Loading trigger details...</div>;
  }

  const webhookUrl = `${API_BASE}/v1/t/${platformUser?.tenant.slug}/webhooks/${triggerId}`;

  return (
    <div className={styles.page}>
      {/* Header section */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <Link to="/triggers" className={styles.backLink}>
            <ArrowLeft size={14} /> Back to Triggers
          </Link>
          <h1 className={styles.title}>
            <Radio size={24} className="text-primary" /> {trigger.name}
          </h1>
          <p className={styles.subtitle}>
            Type: {trigger.triggerType} · ID: {trigger.id}
          </p>
        </div>
      </div>

      {/* Main split grid */}
      <div className={styles.layout}>
        {/* Left Side: Config Form */}
        <form onSubmit={handleSave} className={styles.panel}>
          <h2 className={styles.panelTitle}>Configuration</h2>

          <div className={styles.fields}>
            <div className={styles.field}>
              <label className={styles.label}>Name</label>
              <input
                required
                className={styles.input}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>AI Agent</label>
              <select
                required
                className={styles.select}
                value={form.agentId}
                onChange={(e) => setForm((f) => ({ ...f, agentId: e.target.value }))}
              >
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.fieldFull}>
              <label className={styles.label}>Description</label>
              <input
                className={styles.input}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Active status</label>
              <select
                className={styles.select}
                value={form.active ? 'true' : 'false'}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === 'true' }))}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            {/* Config: SCHEDULED */}
            {trigger.triggerType === 'SCHEDULED' && (
              <>
                <div className={styles.field}>
                  <label className={styles.label}>Cron Expression</label>
                  <input
                    required
                    className={styles.input}
                    value={form.config.cronExpression || ''}
                    onChange={(e) => setConfigValue('cronExpression', e.target.value)}
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Timezone</label>
                  <input
                    required
                    className={styles.input}
                    value={form.config.timezone || ''}
                    onChange={(e) => setConfigValue('timezone', e.target.value)}
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Source App identifier</label>
                  <input
                    required
                    className={styles.input}
                    value={form.config.sourceApp || ''}
                    onChange={(e) => setConfigValue('sourceApp', e.target.value)}
                  />
                </div>
                <div className={styles.fieldFull}>
                  <label className={styles.label}>Initial Conversation Message</label>
                  <textarea
                    required
                    className={styles.textarea}
                    value={form.config.initialMessage || ''}
                    onChange={(e) => setConfigValue('initialMessage', e.target.value)}
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Config: EVENT */}
            {trigger.triggerType === 'EVENT' && (
              <>
                <div className={styles.field}>
                  <label className={styles.label}>Event Type</label>
                  <input
                    required
                    className={styles.input}
                    value={form.config.eventType || ''}
                    onChange={(e) => setConfigValue('eventType', e.target.value)}
                  />
                </div>
                <div className={styles.fieldFull}>
                  <label className={styles.label}>Initial Message</label>
                  <textarea
                    required
                    className={styles.textarea}
                    value={form.config.initialMessage || ''}
                    onChange={(e) => setConfigValue('initialMessage', e.target.value)}
                    rows={3}
                  />
                </div>
              </>
            )}

            {/* Config: WEBHOOK */}
            {trigger.triggerType === 'WEBHOOK' && (
              <>
                <div className={styles.field}>
                  <label className={styles.label}>Endpoint Path slug</label>
                  <input
                    required
                    className={styles.input}
                    value={form.config.endpointPath || ''}
                    onChange={(e) => setConfigValue('endpointPath', e.target.value)}
                  />
                </div>
                <div className={styles.fieldFull}>
                  <label className={styles.label}>Initial Message template</label>
                  <textarea
                    required
                    className={styles.textarea}
                    value={form.config.messageTemplate || ''}
                    onChange={(e) => setConfigValue('messageTemplate', e.target.value)}
                    rows={3}
                  />
                </div>
              </>
            )}
          </div>

          {/* Webhook URL and Secret copy box */}
          {trigger.triggerType === 'WEBHOOK' && (
            <div className={styles.webhookInfoBox}>
              <span className={styles.webhookTitle}>Webhook endpoint</span>
              <div className={styles.copyGroup}>
                <div className={styles.copyInput} title={webhookUrl}>{webhookUrl}</div>
                <button type="button" className={styles.copyBtn} onClick={handleCopyUrl}>
                  {copiedUrl ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>

              <span className={styles.webhookTitle}>Signature Secret</span>
              <div className={styles.copyGroup}>
                <input
                  type={showSecret ? 'text' : 'password'}
                  readOnly
                  className={`${styles.copyInput} font-mono`}
                  value={form.config.secret || ''}
                />
                <button type="button" className={styles.copyBtn} onClick={() => setShowSecret(!showSecret)}>
                  {showSecret ? <EyeOff size={12} /> : <Eye size={12} />}
                </button>
                <button type="button" className={styles.copyBtn} onClick={handleCopySecret}>
                  {copiedSecret ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
              <p className={styles.subtitle} style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                Use this secret to validate headers: <code>X-Enska-Signature: sha256=&lt;hmac&gt;</code>
              </p>
            </div>
          )}

          <div className={styles.actionsRow}>
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              <Trash size={14} style={{ marginRight: '0.25rem' }} /> Delete
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className={styles.saveBtn}
            >
              <Save size={14} style={{ marginRight: '0.25rem' }} /> Save Changes
            </button>
          </div>
        </form>

        {/* Right Side: Executions History */}
        <div className={styles.execsPanel}>
          <h2 className={styles.panelTitle}>Recent Executions</h2>
          <div className={styles.execList}>
            {executions.length === 0 ? (
              <div className={styles.empty}>No executions yet.</div>
            ) : (
              executions.map((exec) => (
                <div key={exec.id} className={styles.execCard}>
                  <div className={styles.execHeader}>
                    <span className={styles.execId}>{exec.id.slice(0, 8)}...</span>
                    <span className={`${styles.execStatus} ${styles[`status${exec.status}`]}`}>
                      {exec.status}
                    </span>
                  </div>
                  <span className={styles.execTime}>
                    Fired: {new Date(exec.createdAt).toLocaleString()}
                  </span>
                  {exec.error && (
                    <span className={styles.execError}>
                      Error: {exec.error}
                    </span>
                  )}
                  {exec.conversationId && (
                    <Link
                      to="/conversations/$conversationId"
                      params={{ conversationId: exec.conversationId }}
                      className={styles.convLink}
                    >
                      <Zap size={10} /> View Conversation
                    </Link>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={() => { setDeleteConfirmOpen(false); deleteMutation.mutate(); }}
        title="Delete Trigger"
        description="Are you sure you want to delete this trigger? This cannot be undone."
        confirmLabel="Delete Trigger"
        variant="danger"
      />
    </div>
  );
}
